import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Link
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Utility function to extract and format filename from URI
const getFormattedFilename = (uri: string): string => {
  // Handle empty or invalid URIs
  if (!uri) return 'No filename available';
  
  // Remove any trailing brackets with numbers (e.g., [3])
  uri = uri.replace(/\[\d+\]$/, '');
  
  // Get the part after the last '/'
  const filename = uri.split('/').pop() || '';
  
  // Decode URI components to handle %20 and other encoded characters
  const decodedFilename = decodeURIComponent(filename);
  
  return decodedFilename;
};

// Utility function to detect and convert URLs to links
const parseLinks = (text: string) => {
  if (!text) return '';
  
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <Link
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ wordBreak: 'break-all' }}
        >
          {part}
        </Link>
      );
    }
    return part;
  });
};

interface Citation {
  generatedResponsePart?: {
    textResponsePart?: {
      span?: {
        start?: number;
        end?: number;
      };
      text?: string;
    };
  };
  retrievedReferences?: RetrievedReference[];
}

interface RetrievedReference {
  content?: {
    text?: string;
  };
  location?: {
    s3Location?: {
      uri?: string;
    };
  };
}

interface CollapsibleReferencesProps {
  citations: Citation[];
}

const CollapsibleReferences: React.FC<CollapsibleReferencesProps> = ({
  citations = []
}) => {
  const [expandedPanel, setExpandedPanel] = useState<number | false>(false);

  const handleChange = (panel: number) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  // Safe access helper function
  const safeGetUri = (citation: Citation): string => {
    return citation?.retrievedReferences?.[0]?.location?.s3Location?.uri || 'No URI available';
  };

  const safeGetCitationText = (citation: Citation): string => {
    return citation?.generatedResponsePart?.textResponsePart?.text || 'No citation text available';
  };

  // Function to generate numbered points from text
  const generateNumberedPoints = (text: string, citationIndex: number) => {
    if (!text) return <Typography variant="body2">No content available</Typography>;

    return text
      .split('\n')
      .filter(line => line.trim() !== '')
      .map((line, index) => (
        <Typography
          key={`${citationIndex}-${index}`}
          variant="body2"
          sx={{ pl: 2, py: 0.5 }}
        >
          {line.trim()}
        </Typography>
      ));
  };

  if (!Array.isArray(citations) || citations.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1">No references available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        References
      </Typography>
      {citations.map((citation, index) => {
        const uri = safeGetUri(citation);
        const filename = getFormattedFilename(uri);
        
        return (
          <Accordion
            key={index}
            expanded={expandedPanel === index}
            onChange={handleChange(index)}
            sx={{
              mb: 1,
              '&:before': {
                display: 'none'
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
            >
              <Typography
                variant="body2"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  width: '100%',
                  wordBreak: 'break-all'
                }}
              >
                <Typography variant="body2" noWrap>
                  [{index + 1}]: {filename}
                </Typography>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                  Source Details
                </Typography>
                {citation.retrievedReferences?.map((ref, refIndex) => (
                  <Box key={refIndex} sx={{ mb: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ 
                        mt: 1, 
                        fontStyle: 'italic', 
                        color: 'text.secondary',
                        wordBreak: 'break-word'
                      }}
                    >
                      {parseLinks(ref.content?.text || 'No content available')}
                    </Typography>
                  </Box>
                )) || <Typography variant="body2">No references available</Typography>}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default CollapsibleReferences;